<script setup lang="ts">
import { useUserMe } from "~/components/user/composables/useUser";
import { useSupabase } from "~/composables/useSupabase";
import { useNotes } from "~/components/note/composables/useNotes";
import type { Note } from "@asap/shared/schemas/note/crud";
import type { NoteSubmit } from "~/components/note/NoteInput.vue";
import type { ActiveCallInteraction } from "@asap/shared/schemas/call.schema";
import { useActorFields } from "~/composables/useActorFields";

type Props = {
  notes: Note[];
  activeCallInteraction?: ActiveCallInteraction | null;
  talentId?: string;
  companyId?: string;
  contactId?: string;
  missionId?: string;
  qualificationTrainingRequestId?: string;
  medicalCheckupRequestId?: string;
  contractId?: string;
  // Defaults to the current user's workspace. Pass an explicit workspaceId when the note belongs
  // to a request owned by another workspace (e.g. WSM-managed QTR / MCR / substitute bench contract).
  workspaceId?: string;
  draftStorageKey?: string;
  isTitleHidden?: boolean;
};

type Emits = { "update:notes": [notes: Note[]] };

const props = defineProps<Props>();
const emit = defineEmits<Emits>();
const supabase = useSupabase();
const { currentWorkspace } = useUserMe();
const { actorFields } = useActorFields();
const { createNote } = useNotes();

// The call note is created at call start; the placeholder only covers the delay until it is listed.
const hasActiveCallNote = computed(() =>
  props.notes.some((note) => note.call_id === props.activeCallInteraction?.call_id)
);

const getPlaceholderCallNote = (callInteraction: ActiveCallInteraction): Note => ({
  id: "",
  workspace_id: "",
  created_at: new Date().toISOString(),
  updated_at: null,
  content: null,
  title: null,
  summarized_content: null,
  audio_transcription: null,
  created_by: { id: "", ...callInteraction.consultant },
  updated_by: null,
  talent_id: null,
  company_id: null,
  contact_id: null,
  mission_id: null,
  contract_id: null,
  medical_checkup_request_id: null,
  qualification_training_request_id: null,
  onboarding_request_id: null,
  is_service_note: null,
  is_feedback_positive: null,
  feedback: null,
  call_id: callInteraction.call_id,
  type: "manual",
});

// Keyed by call: the recreated note of a call keeps its card mounted while the consultant writes.
const replaceNote = (noteId: string, recreatedNote: Note) => {
  const isListed = props.notes.some((listedNote) => listedNote.id === noteId);
  emit(
    "update:notes",
    isListed
      ? props.notes.map((listedNote) => (listedNote.id === noteId ? recreatedNote : listedNote))
      : [recreatedNote, ...props.notes]
  );
};

const createNoteFn = async ({ content, audio_transcription }: NoteSubmit) => {
  const workspaceId = props.workspaceId ?? currentWorkspace.value?.id;
  if (!workspaceId) throw new Error("Workspace ID is required");

  const note = await createNote({
    talent_id: props.talentId ?? null,
    company_id: props.companyId ?? null,
    contact_id: props.contactId ?? null,
    medical_checkup_request_id: props.medicalCheckupRequestId ?? null,
    qualification_training_request_id: props.qualificationTrainingRequestId ?? null,
    workspace_id: workspaceId,
    contract_id: props.contractId ?? null,
    mission_id: props.missionId ?? null,
    content,
    audio_transcription,
  });

  emit("update:notes", [note, ...props.notes]);

  const { updated_by, updated_at } = actorFields();

  if (props.talentId) {
    await supabase.from("talent").update({ updated_by, updated_at }).eq("id", props.talentId);
  }
  if (props.contactId) {
    await supabase.from("contact").update({ updated_by, updated_at }).eq("id", props.contactId);
  }
};
</script>

<template>
  <div class="flex flex-col gap-4 p-2">
    <div v-if="!isTitleHidden">
      <span class="text-lg font-bold">{{ $t("notes.list.title") }}</span>
    </div>

    <NoteInput :key="draftStorageKey" :draft-storage-key="draftStorageKey" @on-submit="createNoteFn($event)" />

    <NoteResultV2
      v-if="activeCallInteraction && !hasActiveCallNote"
      :note="getPlaceholderCallNote(activeCallInteraction)"
      is-fake-note
      :fake-call-is-ongoing="activeCallInteraction?.is_ongoing"
    />

    <NoteResultV2
      v-for="note in notes"
      :key="note.call_id ?? note.id"
      :note="note"
      :linked-call-is-ongoing="
        activeCallInteraction && note.call_id === activeCallInteraction.call_id
          ? activeCallInteraction.is_ongoing
          : undefined
      "
      @on-delete="
        emit(
          'update:notes',
          notes.filter((n) => n.id !== note.id)
        )
      "
      @on-recreate="(recreatedNote) => replaceNote(note.id, recreatedNote)"
    />
  </div>
</template>
